/**
 * ClaimSync Terms of Service Page
 * Matching homepage design
 */

import React, { useEffect } from 'react';
import PublicShell from '../components/marketing/PublicShell';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  const lastUpdated = 'December 24, 2024';

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        header.style.boxShadow = window.pageYOffset > 100 ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <PublicShell wide>

      {/* Hero Section */}
      <section className="pt-6 pb-12">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <h1 className="font-heading text-4xl tracking-tight text-text-primary md:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 lg:py-16">
        <div className="border-t border-hairline pt-10">
          <div className="max-w-none">
            <Section title="1. Acceptance of Terms">
              <p>
                By accessing or using The Figure Co. ("Service"), you agree to be bound by these Terms of Service ("Terms").
                If you do not agree to these Terms, you may not use the Service.
              </p>
              <p>
                These Terms apply to all users, including administrators, team members, and any other individuals 
                who access or use the Service.
              </p>
            </Section>

            <Section title="2. Description of Service">
              <p>The Figure Co. is a business interruption claim calculation platform that provides:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Standard Business Interruption (BI) calculations</li>
                <li>Financial data analysis and processing</li>
                <li>AI-powered transaction classification</li>
                <li>Monte Carlo risk simulations</li>
                <li>Professional report generation</li>
                <li>Team collaboration features</li>
              </ul>
            </Section>

            <Section title="3. Account Registration">
              <p>To use The Figure Co., you must:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
              </ul>
              <p className="mt-4">You are responsible for all activities that occur under your account.</p>
            </Section>

            <Section title="4. Acceptable Use">
              <p>You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Service for any unlawful purpose</li>
                <li>Upload false, misleading, or fraudulent data</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Violate the intellectual property rights of others</li>
                <li>Share account credentials with unauthorized parties</li>
              </ul>
            </Section>

            <Section title="5. Data and Content">
              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Your Data</h4>
              <p>
                You retain ownership of all data and content you upload to The Figure Co. By using the Service,
                you grant us a limited license to process your data solely for the purpose of providing the Service.
              </p>
              
              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Data Accuracy</h4>
              <p>
                You are responsible for the accuracy and legality of data you upload. The Figure Co. processes
                data as provided and does not verify the accuracy of input data.
              </p>

              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Generated Reports</h4>
              <p>
                Reports generated by The Figure Co. are based on the data you provide and standard calculation methodologies.
                These reports are tools to assist professionals and should be reviewed before use in any legal or formal proceedings.
              </p>
            </Section>

            <Section title="6. Professional Responsibility">
              <p>The Figure Co. is a calculation and analysis tool. Users acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Service does not provide legal, accounting, or professional advice</li>
                <li>Users are responsible for verifying all calculations and outputs</li>
                <li>Professional judgment should be applied to all generated reports</li>
                <li>Users should consult qualified professionals as appropriate</li>
              </ul>
            </Section>

            <Section title="7. Intellectual Property">
              <p>
                The Figure Co. and its original content, features, and functionality are owned by The Figure Co., Inc.
                and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-4">
                You may not copy, modify, distribute, sell, or lease any part of the Service without our written permission.
              </p>
            </Section>

            <Section title="8. Service Availability">
              <p>
                We strive to maintain Service availability but do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Scheduled maintenance</li>
                <li>System updates and improvements</li>
                <li>Technical issues beyond our control</li>
                <li>Force majeure events</li>
              </ul>
            </Section>

            <Section title="9. Limitation of Liability">
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Service is provided "AS IS" without warranties of any kind</li>
                <li>We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose</li>
                <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount you paid for the Service in the past 12 months</li>
              </ul>
            </Section>

            <Section title="10. Indemnification">
              <p>
                You agree to indemnify and hold harmless The Figure Co., its officers, directors, employees, and agents
                from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your 
                use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </Section>

            <Section title="11. Termination">
              <p>
                We may terminate or suspend your account and access to the Service at our sole discretion, 
                without prior notice, for conduct that we believe:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Violates these Terms</li>
                <li>Is harmful to other users or the Service</li>
                <li>Is fraudulent or illegal</li>
              </ul>
              <p className="mt-4">Upon termination, your right to use the Service will immediately cease.</p>
            </Section>

            <Section title="12. Changes to Terms">
              <p>
                We reserve the right to modify these Terms at any time. We will provide notice of material changes 
                by posting the updated Terms on this page and updating the "Last updated" date. 
                Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </Section>

            <Section title="13. Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, 
                United States, without regard to its conflict of law provisions. Any disputes shall be resolved 
                in the courts located in Delaware.
              </p>
            </Section>

            <Section title="14. Contact Information">
              <p>For questions about these Terms, please contact us:</p>
              <div className="mt-4 border-l-2 border-hairline pl-4">
                <p className="font-body text-sm leading-relaxed text-text-secondary">
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-dim">Email</span> legal@claimsync.io<br />
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-dim">Address</span> The Figure Co., Inc.<br />
                  <Link to="/contact" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-primary hover:decoration-current">Contact Form</Link>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-heading text-2xl tracking-tight text-text-primary">{title}</h2>
      <div className="space-y-3 font-body text-sm leading-relaxed text-text-secondary">{children}</div>
    </section>
  );
}
